import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

import type { Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "subtle";
type Size = "sm" | "md" | "lg";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-xl font-medium whitespace-nowrap transition-colors " +
  "disabled:opacity-50";

const VARIANTS: Record<Tone, Record<Variant, string>> = {
  app: {
    primary: "bg-brand text-brand-ink not-disabled:hover:bg-brand-strong",
    secondary: "border border-line bg-surface text-ink not-disabled:hover:bg-surface-raised",
    ghost: "text-ink-muted not-disabled:hover:bg-surface-raised not-disabled:hover:text-ink",
    danger: "bg-danger text-white not-disabled:hover:bg-danger-strong",
    subtle: "bg-surface-sunken text-ink not-disabled:hover:bg-surface-raised",
  },
  stage: {
    primary: "bg-brand text-brand-ink not-disabled:hover:bg-brand-strong",
    secondary:
      "border border-stage-line bg-stage-wash text-stage-ink not-disabled:hover:bg-stage-wash-strong",
    ghost: "text-stage-ink/80 not-disabled:hover:bg-stage-wash not-disabled:hover:text-stage-ink",
    danger: "bg-danger text-white not-disabled:hover:bg-danger-strong",
    subtle: "bg-stage-wash text-stage-ink not-disabled:hover:bg-stage-wash-strong",
  },
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant | undefined;
  size?: Size | undefined;

  tone?: Tone | undefined;

  loading?: boolean | undefined;

  ref?: Ref<HTMLButtonElement> | undefined;
  children: ReactNode;
};

export function Button({
  variant = "primary",
  size = "md",
  tone: which = "app",
  loading = false,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      className={cn(BASE, VARIANTS[which][variant], SIZES[size], className)}
      disabled={disabled === true || loading}
      type={type}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
